import React from "react";
import PropTypes from "prop-types";
import styled from "styled-components";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCaretDown, faCaretUp } from "@fortawesome/free-solid-svg-icons";
import localize from "../util/localizeUtils";

const Select = styled.select`
  background-color: ${props => props.theme.input.background};
  border: 1px solid ${props => props.theme.input.border};
  color: ${props => props.theme.input.text};
  cursor: pointer;
  flex: 1;
  font-family: Verdana;
  font-size: 11px;
  height: 24px;
  text-overflow: ellipsis;
  width: 100%;
`;

const LabelContainer = styled.div`
  align-items: center;
  background-color: ${props =>
    props.disabled
      ? props.theme.input.backgroundDisabled
      : props.theme.input.background};
  border: 1px solid ${props => props.theme.input.border};
  color: ${props =>
    props.disabled ? props.theme.text.disabled : props.theme.input.text};
  cursor: ${props => (props.disabled ? "default" : "pointer")};
  display: flex;
  flex-direction: row;
  font-size: 11px;
  height: 1.55rem;
  padding-right: 1rem;

  background-image: linear-gradient(
      45deg,
      transparent 50%,
      ${props => props.theme.text.primary} 50%
    ),
    linear-gradient(
      135deg,
      ${props => props.theme.text.primary} 50%,
      transparent 50%
    ),
    linear-gradient(to right, transparent, transparent);
  background-position: calc(100% - 10px) 50%, calc(100% - 5px) 50%, 100% 0;
  background-size: 5px 5px, 5px 5px, 2.5em 2.5em;
  background-repeat: no-repeat;
`;

const Label = styled.span`
  color: ${props => props.theme.text.secondary};
  padding-right: 4px;
`;

const Option = styled.option`
  display: none;
`;

const List = styled.ul`
  background: ${props => props.theme.input.background};
  border-style: outset;
  border-width: 1px;
  color: ${props => props.theme.input.text};
  display: ${props => (props.isOpen ? "block" : "none")};
  list-style: none;
  margin: 0;
  max-height: 154px;
  outline: rgba(118, 155, 215, 1);
  overflow-x: hidden;
  overflow-y: auto;
  padding: 1px 0;
  position: absolute;
  z-index: 2;
`;

const SelectList = styled.select`
  background-color: ${props => props.theme.input.background};
  color: ${props => props.theme.input.text};
  border-style: outset;
  border-width: 1px;
  display: ${props => (props.isOpen ? "block" : "none")};
  list-style: none;
  margin: 0;
  max-height: 154px;
  outline: rgba(118, 155, 215, 1);
  overflow-x: hidden;
  overflow-y: auto;
  padding: 1px 0;
  position: absolute;
  z-index: 2;
`;

const ListItem = styled.option`
  padding: 2px 20px 2px 10px;
  white-space: nowrap;
`;

const ListItemLabel = styled.label`
  align-items: center;
  color: ${props => props.theme.input.text};
  display: flex;
  flex-direction: row;
  width: 100%;
`;

class MultiSelect extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      isOpen: false,
      options: props.options,
      width: 0
    };
  }

  componentDidMount() {
    document.addEventListener("mousedown", this.handleOutsideClick);
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    this.setState({ options: nextProps.options });
  }

  componentDidUpdate() {
    if (this.state.width !== this.selectRef.clientWidth + 2) {
      this.setState({ width: this.selectRef.clientWidth + 2 });
    }
  }

  componentWillUnmount() {
    document.removeEventListener("mousedown", this.handleOutsideClick);
  }

  hasChanges() {
    for (let i = 0; i < this.props.options.length; i++) {
      if (this.props.options[i].selected !== this.state.options[i].selected) {
        return true;
      }
    }

    return false;
  }

  blur = () => {
    this.setState({ isOpen: false }, () => {
      if (this.listRef) {
        this.listRef.blur();
      }
    });
  };

  focus = () => {
    this.setState({ isOpen: true }, () => {
      if (this.listRef) {
        this.listRef.focus();
      }
    });
  };

  onListKeyDown = event => {
    if (event.keyCode === 13) {
      this.onSelectSingleOption(event, event.target.value);
    }
  };

  selectedOptions() {
    if (this.props.selectedValue) {
      return this.state.options.filter(o => o.id === this.props.selectedValue);
    }

    return this.state.options.filter(o => o.selected);
  }

  handleOutsideClick = event => {
    if (this.selectRef && this.selectRef.contains(event.target)) {
      event.preventDefault();
      event.stopPropagation();
    }
    if (this.wrapperRef && !this.wrapperRef.contains(event.target)) {
      if (this.hasChanges()) {
        this.setState({ isOpen: false }, () =>
          this.props.onChange(this.selectedOptions())
        );
      } else {
        this.setState({ isOpen: false });
      }
    }
  };

  toggleOptionsVisibility = event => {
    event.preventDefault();
    event.stopPropagation();
    const isOpen = !this.state.isOpen;
    if (!this.props.single && !isOpen && this.hasChanges()) {
      this.setState({ isOpen }, () =>
        this.props.onChange(this.selectedOptions())
      );
    } else if (isOpen) {
      this.focus();
    } else if (!isOpen) {
      this.blur();
    }
  };

  toggleOption = event => {
    const options = this.state.options.map(option => {
      if (option.id === parseInt(event.target.value, 10)) {
        return {
          ...option,
          selected: !option.selected
        };
      }

      return option;
    });

    this.setState({ options });
  };

  onSelectSingleOption = (event, value) => {
    event.preventDefault();
    event.stopPropagation();
    this.setState({ isOpen: false }, () => {
      this.props.onChange(value);
    });
  };

  renderSingleSelectOptions() {
    return (
      <SelectList
        style={{ width: "auto", minWidth: this.state.width }}
        ref={list => {
          this.listRef = list;
        }}
        isOpen={this.state.isOpen}
        size={Math.min(this.props.options.length, 7)}
        onBlur={this.blur}
        onKeyDown={this.onListKeyDown}
      >
        {this.props.options.map(option => (
          <ListItem
            key={option.id}
            selected={option.id === this.props.selectedValue}
            title={option.name}
            value={option.id}
            onClick={event => this.onSelectSingleOption(event, option.id)}
          >
            {option.name}
          </ListItem>
        ))}
      </SelectList>
    );
  }

  renderOptions() {
    if (this.props.single) {
      return this.renderSingleSelectOptions();
    }

    return (
      <List style={{ width: this.state.width }} isOpen={this.state.isOpen}>
        {this.state.options.map(option => (
          <li
            key={option.id}
            title={option.name}
            style={{ padding: "2px 4px" }}
          >
            <ListItemLabel>
              <input
                type="checkbox"
                checked={option.selected}
                value={option.id}
                onChange={this.toggleOption}
              />
              <span style={{ paddingLeft: 4 }}>{option.name}</span>
            </ListItemLabel>
          </li>
        ))}
      </List>
    );
  }

  render() {
    return (
      <div
        ref={div => {
          this.wrapperRef = div;
        }}
        style={{
          flex: 1,
          maxWidth: this.props.inlineLabel ? "300px" : "100%",
          position: "relative"
        }}
      >
        {this.props.inlineLabel ? (
          <LabelContainer
            disabled={this.props.disabled}
            ref={select => {
              this.selectRef = select;
            }}
            onClick={this.props.disabled ? null : this.toggleOptionsVisibility}
          >
            <div
              style={{
                overflow: "hidden",
                padding: "0 0.5rem",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap"
              }}
            >
              <Label>{this.props.inlineLabel}</Label>
              {this.selectedOptions()
                .map(o => o.name)
                .join(", ") || localize("showAllOption")}
            </div>
          </LabelContainer>
        ) : (
          <Select
            ref={select => {
              this.selectRef = select;
            }}
            onClick={this.toggleOptionsVisibility}
            disabled={this.props.disabled}
            readOnly
          >
            <Option>
              {this.selectedOptions()
                .map(o => o.name)
                .join(", ")}
            </Option>
          </Select>
        )}
        {this.renderOptions()}
      </div>
    );
  }
}

MultiSelect.defaultProps = {
  className: "",
  disabled: false,
  inlineLabel: "",
  selectedValue: "",
  single: false,
  onChange: () => {}
};

MultiSelect.propTypes = {
  disabled: PropTypes.bool,
  inlineLabel: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  options: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
      name: PropTypes.string.isRequired,
      selected: PropTypes.bool
    }).isRequired
  ).isRequired,
  selectedValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  single: PropTypes.bool,
  onChange: PropTypes.func
};

export default MultiSelect;



// WEBPACK FOOTER //
// ./app/styled-components/MultiSelect.js